import { reactive, ref, computed } from 'vue'

/**
 * Product Restoration Manager
 * 
 * Coordinates product position restoration with filter updates to prevent conflicts.
 * Manages restoration state and provides status flags to filter manager and components.
 */
export const useProductRestoration = () => {
  // Restoration state
  const restorationState = reactive({
    isRestoring: false,
    isFilterBarInitializing: false,
    pendingProductRestore: null,
    restoreRetryCount: 0,
    restorationTimeout: null,
    productContext: null,
    pendingAttributesRestore: null,
  })

  /**
   * Start product position restoration
   * @param {Object} context - Restoration context { productId, cacheKey, page, portalPage, timestamp }
   */
  const startRestoration = (context) => {
    if (!context || !context.productId) {
      console.warn('Invalid restoration context provided')
      return false
    }

    restorationState.isRestoring = true
    restorationState.isFilterBarInitializing = true
    restorationState.productContext = {
      productId: context.productId,
      cacheKey: context.cacheKey,
      page: context.page || 1,
      portalPage: context.portalPage || 'products',
      timestamp: context.timestamp || Date.now(),
    }

    // Set safety timeout to auto-clear flags if restoration doesn't complete
    if (restorationState.restorationTimeout) {
      clearTimeout(restorationState.restorationTimeout)
    }
    
    restorationState.restorationTimeout = setTimeout(() => {
      if (restorationState.isRestoring || restorationState.isFilterBarInitializing) {
        console.warn('⚠️ Restoration timeout - clearing flags')
        completeRestoration()
      }
    }, 15000) // 15 second safety timeout

    return true
  }

  /**
   * Set pending product restore (for pagination)
   */
  const setPendingProductRestore = (productId, page) => {
    restorationState.pendingProductRestore = {
      productId,
      page,
    }
  }

  /**
   * Clear pending product restore
   */
  const clearPendingProductRestore = () => {
    restorationState.pendingProductRestore = null
  }

  /**
   * Set pending attributes restore
   */
  const setPendingAttributesRestore = (attributes) => {
    restorationState.pendingAttributesRestore = attributes
  }

  /**
   * Clear pending attributes restore
   */
  const clearPendingAttributesRestore = () => {
    restorationState.pendingAttributesRestore = null
  }

  /**
   * Complete product position restoration
   */
  const completeRestoration = () => {
    // Clear restoration timeout
    if (restorationState.restorationTimeout) {
      clearTimeout(restorationState.restorationTimeout)
      restorationState.restorationTimeout = null
    }

    // Clear flags with delay to allow FilterBar to finish initializing
    setTimeout(() => {
      restorationState.isRestoring = false
      
      // Clear FilterBar flag after additional delay
      setTimeout(() => {
        restorationState.isFilterBarInitializing = false
        restorationState.productContext = null
        restorationState.restoreRetryCount = 0
      }, 2000) // 2 second delay for FilterBar initialization
    }, 3000) // 3 second delay for restoration completion
  }

  /**
   * Check if restoration is currently in progress
   */
  const isRestoring = computed(() => {
    return restorationState.isRestoring || 
           restorationState.isFilterBarInitializing || 
           restorationState.pendingProductRestore !== null
  })

  /**
   * Check if filters can be updated (not during restoration)
   */
  const canUpdateFilters = computed(() => {
    return !isRestoring.value
  })

  /**
   * Get current restoration context
   */
  const getRestorationContext = () => {
    return restorationState.productContext ? { ...restorationState.productContext } : null
  }

  /**
   * Get stored product navigation context from sessionStorage
   */
  const getStoredProductNavigationContext = () => {
    if (typeof window === 'undefined') return null
    
    try {
      const stored = sessionStorage.getItem('lastClickedProduct')
      if (!stored) return null
      
      const context = JSON.parse(stored)
      
      // Check if context is still valid (not too old - 1 hour)
      const age = Date.now() - (context.timestamp || 0)
      const maxAge = 60 * 60 * 1000 // 1 hour
      
      if (age > maxAge) {
        sessionStorage.removeItem('lastClickedProduct')
        return null
      }
      
      return context
    } catch (error) {
      console.error('Failed to get stored product navigation context:', error)
      return null
    }
  }

  /**
   * Store product navigation context in sessionStorage
   */
  const storeProductNavigationContext = (productId, cacheKey, page, portalPage = 'products', filterQueries = {}) => {
    if (typeof window === 'undefined') return
    
    try {
      const context = {
        productId,
        cacheKey,
        page,
        timestamp: Date.now(),
        portalPage,
        // Store filter queries for restoration
        filterQuery: filterQueries.filterQuery || '',
        attributesQuery: filterQueries.attributesQuery || '',
        categoryId: filterQueries.categoryId || '',
        sortBy: filterQueries.sortBy || 'date',
        sortType: filterQueries.sortType || 'desc',
        search: filterQueries.search || '',
      }
      sessionStorage.setItem('lastClickedProduct', JSON.stringify(context))
    } catch (error) {
      console.error('Failed to store product navigation context:', error)
    }
  }

  /**
   * Clear stored product navigation context
   */
  const clearStoredProductNavigationContext = () => {
    if (typeof window === 'undefined') return
    sessionStorage.removeItem('lastClickedProduct')
  }

  /**
   * Increment retry count
   */
  const incrementRetryCount = () => {
    restorationState.restoreRetryCount++
  }

  /**
   * Reset retry count
   */
  const resetRetryCount = () => {
    restorationState.restoreRetryCount = 0
  }

  /**
   * Get retry count
   */
  const getRetryCount = () => {
    return restorationState.restoreRetryCount
  }

  /**
   * Check if restoration should be attempted based on context
   */
  const shouldAttemptRestoration = (portalPage) => {
    const context = getStoredProductNavigationContext()
    
    if (!context) return false
    
    // Check portal page matches
    if (context.portalPage !== portalPage) return false
    
    // Check context age (less than 1 hour)
    const age = Date.now() - (context.timestamp || 0)
    const maxAge = 60 * 60 * 1000 // 1 hour
    
    return age < maxAge
  }

  /**
   * Verify cache key matches restoration context
   */
  const verifyCacheKey = (currentCacheKey) => {
    const context = restorationState.productContext || getStoredProductNavigationContext()
    
    if (!context) return false
    
    return context.cacheKey === currentCacheKey
  }

  return {
    // State
    restorationState,
    isRestoring,
    canUpdateFilters,
    
    // Methods
    startRestoration,
    completeRestoration,
    setPendingProductRestore,
    clearPendingProductRestore,
    setPendingAttributesRestore,
    clearPendingAttributesRestore,
    getRestorationContext,
    getStoredProductNavigationContext,
    storeProductNavigationContext,
    clearStoredProductNavigationContext,
    incrementRetryCount,
    resetRetryCount,
    getRetryCount,
    shouldAttemptRestoration,
    verifyCacheKey,
  }
}

